import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Field,
  Label,
} from "@headlessui/react";
import cx from "classnames";
import { IconChevronDown } from "@hawkins/assets";
import { AnimatePresence } from "motion/react";
import { motion } from "motion/react";
import { Lenis } from "lenis/react";
import { useContext } from "react";
import { FormLabelsContext } from "./FormLabelsContext";

export const Select = ({
  label,
  options,
  onChange,
  required,
  value,
  invalid,
  name,
}: {
  label: string;
  options: Array<{ label: string; value: string }>;
  onChange: (value: string) => void;
  value: string;
  required: boolean;
  invalid: boolean;
  name: string;
}) => {
  const otherLabels = useContext(FormLabelsContext);

  return (
    <Field>
      <div className="flex gap-2 items-center text-body-regular mb-2 ml-3">
        <Label>{label}</Label>
        {required && (
          <span className="italic text-body-small text-brand-cream text-opacity-30">
            {otherLabels.required}
          </span>
        )}
      </div>
      <Listbox value={value} onChange={onChange}>
        {({ open }) => (
          <div className="relative">
            <ListboxButton
              name={name}
              className={cx(
                "flex items-center gap-1 w-full h-14 py-1 px-3 outline-none font-medium border rounded-lg bg-transparent border-brand-cream/30 aria-expanded:bg-brand-black aria-expanded:border-brand-cream focus:border-brand-cream peer",
                // Notably, we cannot transition other properties or at other times
                // because it would conflict with the illusion that is played into*:
                // where the sibling appears to grow out from this element
                !open &&
                  "transition-[background-color] duration-half delay-full",
                invalid &&
                  "!border-brand-red bg-gradient-to-r from-brand-red/15 to-brand-red/15"
              )}
            >
              <span className="block truncate text-brand-cream">
                {options.filter((option) => option.value === value)[0]
                  ?.label || (
                  <span className="opacity-50">
                    {otherLabels.selectPlaceholder}
                  </span>
                )}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
                <IconChevronDown
                  size="small"
                  className={cx("transition-transform", open && "rotate-180")}
                />
              </span>
            </ListboxButton>
            <AnimatePresence>
              {open && (
                <ListboxOptions
                  static
                  className={cx(
                    "absolute z-10 left-0 w-full text-brand-cream rounded-b-lg overflow-clip top-full pt-2 -mt-2 border-x border-b outline-none bg-brand-black border-brand-cream/30 peer-aria-expanded:border-brand-cream peer-focus:border-brand-cream",
                    invalid &&
                      "!border-brand-red bg-gradient-to-r from-brand-red/15 to-brand-red/15"
                  )}
                  as={motion.div}
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                >
                  <Lenis
                    className="max-h-[50vh] overflow-y-auto"
                    options={{ duration: 2 }}
                  >
                    {options.map((option, index) => (
                      <ListboxOption
                        key={index}
                        className="relative cursor-default select-none py-1.5 px-3 duration-half bg-transparent transition-colors data-[focus]:bg-brand-cream/10 text-brand-cream"
                        value={option.value}
                      >
                        {({ selected }) => (
                          <span
                            className={cx(
                              "block truncate",
                              selected ? "font-bold" : "font-medium"
                            )}
                          >
                            {option.label}
                          </span>
                        )}
                      </ListboxOption>
                    ))}
                  </Lenis>
                </ListboxOptions>
              )}
            </AnimatePresence>
          </div>
        )}
      </Listbox>
    </Field>
  );
};
